import React, { useCallback, useEffect, useState } from 'react';
import API from '../../services/api';
import ResearchCrumb from './ResearchCrumb';
import PageHeading from './PageHeading';
import LoadingSpinner from '../../components/LoadingSpinner';

// Chatter — what commentators are saying, summarised.
//
// ONE PAGE, NOT SEVEN TABS. The sending platform splits this across Overview, Narrative,
// Themes and Relationships because the people there are working the pipeline: they need
// per-video state, extraction queues and an editable graph. Nobody reading here operates any
// of that, so the same four blocks are laid out as sections of one scrollable page in
// descending order of what a reader can act on. Tabs would hide three quarters of a summary
// behind clicks that answer no question.
//
// The standing "this is third-party commentary" disclaimer was removed on the owner's
// instruction. What replaces it is concrete rather than a caveat: the Sources row names the one
// feed that is live and counts its channels and transcripts, and the as-at date is shown beside
// the figures. (The heading note says only "What commentators are saying" — it does NOT name
// the source, so the Sources row is the only thing that does. Do not delete it.)

const GOLD = 'rgb(var(--brand-gold-rgb))';
const STANCE = { bullish: '#16a34a', bearish: '#dc2626', neutral: '#9ca3af' };

const num = (v) => (typeof v === 'number' && isFinite(v) ? v : null);

/** Coerce a feed value to something React can render.
 *
 * `a_label`/`b_label` on a co-mention edge are OBJECTS ({name, ticker, sector, ...}), not
 * strings — the contract says "the display objects" and that is literal. Rendering one
 * directly throws "Objects are not valid as a React child", which unmounts the entire page
 * to white rather than degrading the one row that carries it.
 *
 * Everything arriving from the feed that is printed straight into markup goes through here,
 * so a field that changes shape costs a wrong-looking label instead of a blank screen.
 */
function text(v, fallback = '—') {
  if (v == null || v === '') return fallback;
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'object') return v.name || v.ticker || v.label || v.title || fallback;
  return fallback;
}
const int = (v) => (num(v) == null ? '—' : Math.round(v).toLocaleString());

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toISOString().slice(0, 10); } catch { return s; }
}

// What is being listened to, and what is not yet.
//
// NOT a paragraph: a reader needs to know the sweep is currently one source before they
// weigh a favourability number, and that is a fact, not an explanation. The upcoming ones
// are shown greyed rather than hidden so the shape of the thing is legible — otherwise a
// single-source read looks like the finished article.
const SOURCES = [
  { name: 'YouTube', live: true },
  { name: 'X', live: false },
  { name: 'Reddit', live: false },
  { name: 'Company blogs', live: false },
];

function Sources({ channels, transcripts }) {
  return (
    <div>
      <Kicker>Sources</Kicker>
      <div className="scrollbar-thin -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {SOURCES.map((x) => (
          <div
            key={x.name}
            className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] ${x.live
              ? 'border-gray-200 text-gray-700 dark:border-gray-800 dark:text-gray-200'
              : 'border-dashed border-gray-200 text-gray-400 dark:border-gray-800 dark:text-gray-500'}`}
          >
            {x.live && (
              <span className="relative flex h-1.5 w-1.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
              </span>
            )}
            <span className="whitespace-nowrap">{x.name}</span>
            {x.live
              ? <span className="whitespace-nowrap text-[11px] text-gray-400">
                  {int(channels)} channels · {int(transcripts)} transcripts
                </span>
              : <span className="whitespace-nowrap text-[10px] uppercase tracking-[0.15em]">soon</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

const Kicker = ({ children }) => (
  <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em]" style={{ color: GOLD }}>
    {children}
  </div>
);

const Empty = ({ children }) => (
  <p className="text-sm text-gray-500 dark:text-gray-400">{children}</p>
);

// Hairline KPI grid: gap-px over a tinted parent draws 1px rules on both axes with no
// borders on the cells themselves.
const Cell = ({ label, value, sub, lead }) => (
  <div className={`flex min-h-[92px] flex-col justify-between p-4 ${lead
    ? 'bg-gray-50 dark:bg-gray-900/40' : 'bg-white dark:bg-gray-900/70'}`}>
    <div className="text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">{label}</div>
    <div>
      <div className={`font-serif tabular-nums ${lead ? 'text-[28px] leading-none' : 'text-2xl'}`}
           style={lead ? { color: GOLD } : undefined}>{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-gray-400">{sub}</div>}
    </div>
  </div>
);

/** Favourability is signed, in [-1,+1], so it is drawn from the CENTRE outwards.
 *
 * A left-anchored bar would make -0.9 and +0.1 look like the same small quantity and put
 * the most negative name closest to empty. Centre-zero is the only honest track for a value
 * that has a direction as well as a size.
 */
function Favor({ v }) {
  const n = num(v);
  if (n == null) return <div className="h-1.5 w-full rounded bg-gray-100 dark:bg-gray-800" />;
  const half = Math.min(50, Math.abs(n) * 50);
  const positive = n >= 0;
  return (
    <div className="relative h-1.5 w-full rounded bg-gray-100 dark:bg-gray-800">
      <div className="absolute inset-y-0 rounded"
           style={{
             left: positive ? '50%' : `${50 - half}%`,
             width: `${Math.max(1, half)}%`,
             background: positive ? STANCE.bullish : STANCE.bearish,
             opacity: 0.75,
           }} />
      <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
    </div>
  );
}

const Momentum = ({ v }) => {
  const n = num(v);
  if (n == null) return <span className="text-gray-400">—</span>;
  if (Math.abs(n) < 0.005) return <span className="tabular-nums text-gray-400">flat</span>;
  return (
    <span className={`tabular-nums font-medium ${n > 0
      ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
      {n > 0 ? '+' : ''}{n.toFixed(2)}
    </span>
  );
};

const StanceChip = ({ stance }) => {
  const c = STANCE[stance] || STANCE.neutral;
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium"
          style={{ background: `${c}1a`, color: c }}>
      {stance || 'neutral'}
    </span>
  );
};

function CompanyRow({ c }) {
  const bull = num(c.bull) || 0;
  const bear = num(c.bear) || 0;
  const neutral = num(c.neutral) || 0;
  return (
    <div className="grid grid-cols-[1fr_5.5rem] items-center gap-3 border-t border-gray-100 py-2 dark:border-gray-800/60">
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
            {text(c.ticker, text(c.name))}
          </span>
          {c.ticker && c.name
            ? <span className="truncate text-xs text-gray-400">{text(c.name, '')}</span> : null}
        </div>
        <div className="mt-1"><Favor v={c.favorability} /></div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 text-[10px] text-gray-400">
          <span className="tabular-nums">{int(c.mentions)} mentions</span>
          <span className="tabular-nums">{bull} bull · {bear} bear · {neutral} neutral</span>
        </div>
      </div>
      <div className="text-right text-xs">
        <div className="tabular-nums text-gray-500 dark:text-gray-400">
          {num(c.favorability) == null ? '—' : num(c.favorability).toFixed(2)}
        </div>
        <div className="mt-0.5"><Momentum v={c.momentum} /></div>
      </div>
    </div>
  );
}

function CompanyList({ title, note, rows }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">{title}</h3>
        <span className="text-[11px] text-gray-400">{note}</span>
      </div>
      <div className="mt-1">
        {rows.map((c, i) => <CompanyRow key={`${c.ticker || c.name}-${i}`} c={c} />)}
      </div>
    </div>
  );
}

function Takeaway({ t }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-gray-200 p-4 pl-5 transition-colors hover:border-brand-gold/50 dark:border-gray-800">
      <span className="absolute inset-y-0 left-0 w-[3px]" style={{ background: GOLD, opacity: 0.55 }} />
      <div className="flex items-baseline justify-between gap-2 text-[10px] text-gray-400">
        <span className="truncate">{text(t.source_ref)}</span>
        <span className="whitespace-nowrap tabular-nums">{fmtDate(t.published_at)}</span>
      </div>
      {t.thesis && (
        <p className="mt-1.5 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
          {text(t.thesis, '')}
        </p>
      )}
      {!!(t.companies || []).length && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {t.companies.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {text(c.ticker, text(c.name))}
              </span>
              <StanceChip stance={c.stance} />
            </span>
          ))}
        </div>
      )}
      {!!(t.topics || []).length && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {t.topics.map((tp, i) => (
            <span key={i} className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-500 dark:border-gray-800 dark:text-gray-400">
              {text(tp.title, '')}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ChatterPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    API.get('/api/research-feed/chatter/')
      .then((r) => { setData(r.data); setErr(''); })
      .catch(() => setErr('Chatter could not be loaded.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const c = data?.chatter || null;
  const counts = c?.overview?.counts || {};
  const recent = c?.overview?.recent || [];
  const narrative = c?.narrative || {};
  const buckets = c?.themes?.buckets || [];
  const edges = c?.relationships?.companies?.edges || [];
  const shown = showAll ? recent : recent.slice(0, 3);

  return (
    <div>
      <ResearchCrumb mode="Chatter" />

      {/* The shared mode heading, so this page and Quorum are visibly siblings. The existing
          one-line note stays — the owner wrote it and it is not being reintroduced here, it
          was never removed; it just moves inside the heading block so it sits at the pattern's
          spacing rather than hanging off a title it no longer touches. */}
      <PageHeading title="Chatter" note="What commentators are saying." />

      {err && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          {err}
        </div>
      )}

      {loading && <LoadingSpinner message="Loading Chatter…" />}

      {!loading && !c && !err && (
        <Empty>No Chatter summary has been received yet — it appears here once the sender includes one.</Empty>
      )}

      {!loading && c && (
        <div className="flex flex-col gap-6">
          {data?.as_at && (
            <p className="text-[11px] text-gray-400">As at {fmtDate(data.as_at)}.</p>
          )}

          <Sources channels={counts.channels} transcripts={counts.transcripts} />

          <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800">
            <div className="grid grid-cols-2 gap-px bg-gray-200 sm:grid-cols-4 dark:bg-gray-800">
              {/* Two different things, both worth knowing: how many companies people talked
                  about, and how many of those are names with a US listing.
                  "COVERED" WAS THE WRONG WORD and is not used here. The sender's field is
                  companies_covered and its own model defines it as "in our security master
                  (ticker + GICS + priceable)" — the rest "could be private, foreign-listed,
                  or" simply untracked. So it means US-listed, not researched or vetted, and
                  a reader would have read it as the latter. Say the concrete thing. */}
              <Cell lead label="Companies mentioned" value={int(counts.companies)}
                    sub={`${int(counts.companies_covered)} US-listed`} />
              <Cell label="Transcripts" value={int(counts.transcripts)} sub={`${int(counts.channels)} channels`} />
              <Cell label="Claims" value={int(counts.claims)} sub="extracted" />
              <Cell label="Relationships" value={int(counts.relationships)} sub="observed" />
            </div>
          </div>

          {(narrative.winners?.length || narrative.losers?.length) ? (
            <div>
              <Kicker>Sentiment</Kicker>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <CompanyList title="Most favoured" note="favourability · momentum"
                             rows={narrative.winners} />
                <CompanyList title="Least favoured" note="favourability · momentum"
                             rows={narrative.losers} />
              </div>
            </div>
          ) : null}

          {!!narrative.movers?.length && (
            <div>
              <Kicker>Biggest shifts</Kicker>
              <CompanyList title="Moved most" note="change vs the prior window"
                           rows={narrative.movers} />
            </div>
          )}

          {!!recent.length && (
            <div>
              <Kicker>Latest takeaways</Kicker>
              <div className="flex flex-col gap-3">
                {shown.map((t, i) => <Takeaway key={i} t={t} />)}
              </div>
              {recent.length > 3 && (
                <button
                  type="button" onClick={() => setShowAll((v) => !v)}
                  className="mt-3 inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] font-medium text-gray-600 transition-colors hover:border-brand-gold hover:text-brand-gold dark:border-gray-800 dark:text-gray-300"
                >
                  {showAll ? '▲ Show less' : `▼ Show ${recent.length - 3} more`}
                </button>
              )}
            </div>
          )}

          {!!buckets.length && (
            <div>
              <Kicker>Themes</Kicker>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {buckets.map((b, i) => (
                  <div key={i} className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-serif text-base text-gray-900 dark:text-gray-100">
                        {text(b.label)}
                      </span>
                      <span className="font-serif text-lg tabular-nums" style={{ color: GOLD }}>{b.count}</span>
                    </div>
                    {!!(b.themes || []).length && (
                      <p className="mt-1.5 line-clamp-3 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
                        {b.themes.slice(0, 6).map((x) => text(x, '')).filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!!edges.length && (
            <div>
              <Kicker>Mentioned together</Kicker>
              <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
                {edges.slice(0, 12).map((e, i) => (
                  <div key={i} className="grid grid-cols-[1fr_auto] items-center gap-3 border-t border-gray-100 py-2 first:border-t-0 dark:border-gray-800/60">
                    <div className="min-w-0 truncate text-sm text-gray-800 dark:text-gray-200">
                      {text(e.a_label, text(e.a))}
                      <span className="mx-1.5 text-gray-300 dark:text-gray-600">+</span>
                      {text(e.b_label, text(e.b))}
                    </div>
                    <div className="whitespace-nowrap text-[11px] tabular-nums text-gray-400">
                      {int(e.cooccur)} videos
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
